import * as React from "react";
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';

import { Flex } from "@common/components/Flex";
import { Button } from "@common/components/Button";
import { getTestAttributes } from "@common/utils/tests";
import { MODULE_NAME } from "@modules/invite-status/constants/shared";
import { HttpErrorDto } from "@common/dto/http";

import { InvitesExportSettings } from "./export-settings";
import { InvitesExportType, InvitesExportOptionKeys } from "./invites-export.types";
import { InvitesExportOptions } from "./export-options";

import "./invites-export.styles.scss";

export type InvitesExportProps = {
  onSubmit: ({
    options,
    setting,
  }: {
    options: Array<InvitesExportOptionKeys>;
    setting: {
      exportType: InvitesExportType;
      delimiter?: string;
    };
  }) => Promise<void>;
  onClose: () => void;
  showErrorMessage: (message: string) => void;
};

export const InvitesExport: React.FC<InvitesExportProps> = ({ onClose, onSubmit, showErrorMessage }) => {
  const [selectedOptions, setSelectedOptions] = React.useState<Array<InvitesExportOptionKeys>>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedSetting, setSelectedSetting] = React.useState<{
    exportType: InvitesExportType;
    delimiter?: string;
  }>({ exportType: InvitesExportType.tabDelimited });

  const handleOnSubmit = React.useCallback(async () => {
    try {
      setSubmitting(true);
      await onSubmit({ options: selectedOptions, setting: selectedSetting });
    } catch (err) {
      console.error(err);
      const errorMessage = (err as HttpErrorDto)?.entity || I18n.t("Failed to export invites data");
      showErrorMessage(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }, [selectedOptions, selectedSetting, onSubmit]);

  const exportBtnDisabled = submitting || (selectedSetting.exportType === InvitesExportType.other && !selectedSetting.delimiter);

  return (
    <Modal
      show
      bsSize="small"
      bsClass="InvitesExport bootstrap modal"
      onHide={onClose}
      onEscapeKeyDown={onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle {...getTestAttributes(MODULE_NAME, "invites-export", "title")}>
          {I18n.t("Export Data")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Flex justify="space-between">
          <div className="InvitesExport__config">
            <InvitesExportSettings value={selectedSetting} onChange={setSelectedSetting} />
            <InvitesExportOptions value={selectedOptions} onChange={setSelectedOptions} />
          </div>
          <div>
            <Button disabled={exportBtnDisabled} {...getTestAttributes(MODULE_NAME, "invites-export", "button")} onClick={handleOnSubmit}>
              {submitting ? I18n.t("Loading") : I18n.t("Export")}
            </Button>
          </div>
        </Flex>
      </ModalBody>
    </Modal>
  );
};
